import { StarRounded } from '@mui/icons-material';
import { Box, LinearProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { useI18n } from '../../../../../../../application/language/useI18n';
import { IUserDataModel, UserDataLevel } from '../../../../../service/userData/IUserDataModel';

export interface IUserInfoCard {
  fio: string;
  role: string | null;
  userData: IUserDataModel;
}

export const UserInfoCard = observer(
  ({ userData: { toNextLevelScore, level, totalScore, maxScore, levelThresholds } }: IUserInfoCard) => {
    const i18n = useI18n();
    const [nextLevelScore, setNextLevelScore] = useState<string>('');

    useEffect(() => {
      if (totalScore === 0) {
        setNextLevelScore(i18n.translate('userCardInfo.learnAndLevelUp'));
      } else if (toNextLevelScore === 0) {
        setNextLevelScore(
          maxScore === 0
            ? i18n.translate('userCardInfo.learnAndLevelUp')
            : i18n.translate('userCardInfo.allUnitsPassed'),
        );
      } else if (level === UserDataLevel.SENIOR) {
        setNextLevelScore(`${i18n.translate('userCardInfo.toMax')} ${toNextLevelScore}`);
      } else {
        setNextLevelScore(`${i18n.translate('userCardInfo.toNextLevel')} ${toNextLevelScore}`);
      }
    }, [toNextLevelScore, totalScore, maxScore, level]);

    const highlightColor = 'rgba(144, 202, 249, 1)';
    const inactiveColor = 'rgba(255,255,255,0.5)';

    const getLevelColors = (targetLevel: 'junior' | 'middle' | 'senior') => {
      let progress = 0;

      if (targetLevel === 'junior') {
        progress =
          level === UserDataLevel.SENIOR
            ? 100
            : level === UserDataLevel.MIDDLE
            ? 100
            : totalScore === 0
            ? 0
            : Math.round(totalScore / ((maxScore * 0.35) / 100));
      }

      if (targetLevel === 'middle') {
        
        progress =
          level === UserDataLevel.SENIOR
            ? 100
            : level === UserDataLevel.MIDDLE
            ? Math.round((totalScore - (maxScore * 35) / 100) / ((maxScore * 0.34) / 100))
            : 0;
      }

      if (targetLevel === 'senior') {
        progress =
          level === UserDataLevel.SENIOR
            ? toNextLevelScore === 0
              ? 100
              : Math.round((totalScore - Math.round((maxScore * 69) / 100 + 1)) / ((maxScore * 0.31) / 100))
            : 0;
      }

      const isFull = Math.round(progress) >= 100;

      const reachedNewLevel =
        (targetLevel === 'middle' && level === UserDataLevel.MIDDLE) ||
        (targetLevel === 'senior' && level === UserDataLevel.SENIOR);

      const isPartial = (progress > 0 && Math.round(progress) < 100) || reachedNewLevel;

      return {
        rankColor: isFull || isPartial ? highlightColor : inactiveColor,
        scoreColor: isFull ? highlightColor : inactiveColor,
      };
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
              <StarRounded
                sx={{
                  width: '12px',
                  height: '12px',
                  background: highlightColor,
                  borderRadius: '50%',
                  color: '#29363D',
                }}
              />
              <Typography sx={{ fontSize: '14px', color: inactiveColor }}>{totalScore}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: inactiveColor }}>{nextLevelScore}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: '8px' }}>
            {/* JUNIOR --- */}
            {(() => {
              const { rankColor, scoreColor } = getLevelColors('junior');
              const value =
                level === UserDataLevel.SENIOR
                  ? 100
                  : level === UserDataLevel.MIDDLE
                  ? 100
                  : totalScore === 0
                  ? 0
                  : Math.round(totalScore / ((maxScore * 0.35) / 100));

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                  <LinearProgress variant="determinate" value={value} sx={{ width: '117px' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ color: rankColor, textAlign: 'center', fontSize: '14px' }}>
                      {i18n.translate('userCardInfo.junior')}
                    </Typography>
                    <Typography sx={{ color: scoreColor, fontSize: '14px' }}>{levelThresholds?.junior}</Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* MIDDLE -- */}
            {(() => {
              const { rankColor, scoreColor } = getLevelColors('middle');
              const value =
                level === UserDataLevel.SENIOR
                  ? 100
                  : level === UserDataLevel.MIDDLE
                  ? Math.round((totalScore - (maxScore * 35) / 100) / ((maxScore * 0.34) / 100))
                  : 0;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                  <LinearProgress variant="determinate" value={value} sx={{ width: '117px' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ color: rankColor, textAlign: 'center', fontSize: '14px' }}>
                      {i18n.translate('userCardInfo.middle')}
                    </Typography>
                    <Typography sx={{ color: scoreColor, fontSize: '14px' }}>{levelThresholds?.middle}</Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* SENIOR --- */}
            {(() => {
              const { rankColor, scoreColor } = getLevelColors('senior');
              const value =
                level === UserDataLevel.SENIOR
                  ? toNextLevelScore === 0
                    ? 100
                    : Math.round((totalScore - Math.round((maxScore * 69) / 100 + 1)) / ((maxScore * 0.31) / 100))
                  : 0;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                  <LinearProgress variant="determinate" value={value} sx={{ width: '117px' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ color: rankColor, textAlign: 'center', fontSize: '14px' }}>
                      {i18n.translate('userCardInfo.senior')}
                    </Typography>
                    <Typography sx={{ color: scoreColor, fontSize: '14px' }}>{levelThresholds?.senior}</Typography>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Box>
    );
  },
);
